import { useState } from 'react';
import useProjectStore from '../stores/projectStore';
import { useAgentReport } from '../hooks/useAgentReport';
import { useAgentIntent } from '../hooks/useAgentLibrary';
import { useSettings } from '../hooks/useSettings';
import api from '../lib/axios';
import ReportView from '../components/ReportView';

const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  #report-print, #report-print * { visibility: visible !important; }
  #report-print { position: absolute; left: 0; top: 0; width: 100%; }
  .no-print { display: none !important; }
}`;

export default function Report() {
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const projects = useProjectStore((s) => s.projects);
  const current = projects.find((p) => p.id === currentProjectId);
  const { data: settings } = useSettings();

  const { data, isLoading, refetch } = useAgentReport(currentProjectId);
  const intent = useAgentIntent();
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const report = data?.report;
  const accent = current?.color || '#0C9488';
  const logo = settings?.logo_url;

  const build = async () => {
    setBusy(true);
    try {
      await intent.mutateAsync({ intent: 'project.report' });
      await refetch();
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const share = async () => {
    try {
      const { data: res } = await api.post('/agent-report/share');
      setShareUrl(res.url);
    } catch { /* ignore */ }
  };

  const copy = () => {
    if (shareUrl) {
      navigator.clipboard?.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto">
      <style>{PRINT_CSS}</style>

      <div className="flex items-center justify-between gap-4 mb-3 no-print">
        <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">Gesamtreport</h1>
        <div className="flex gap-2 flex-wrap justify-end">
          <button onClick={build} disabled={busy} className="text-[13px] px-3.5 py-2 bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap">
            {busy ? 'Erstelle…' : 'Report erstellen'}
          </button>
          {report && (
            <>
              <button onClick={share} className="text-[13px] px-3.5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap">Teilen</button>
              <a href={`/api/agent-report/pdf?project=${currentProjectId}`} target="_blank" rel="noreferrer" className="text-[13px] px-3.5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap">PDF</a>
              <button onClick={() => window.print()} className="text-[13px] px-3.5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap">Drucken</button>
            </>
          )}
        </div>
      </div>

      {shareUrl && (
        <div className="no-print mb-4 flex items-center gap-2 bg-teal-light/50 border border-teal/20 rounded-lg px-3 py-2">
          <span className="text-[12px] text-gray-500 shrink-0">Öffentlicher Link:</span>
          <input readOnly value={shareUrl} className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-[12px] text-gray-700" onFocus={(e) => e.target.select()} />
          <button onClick={copy} className="text-[12px] px-2.5 py-1 bg-[#0D3B50] text-white rounded shrink-0">{copied ? 'Kopiert ✓' : 'Kopieren'}</button>
        </div>
      )}

      {isLoading ? (
        <div className="text-[13px] text-gray-500 p-6">Lade…</div>
      ) : ! report ? (
        <div className="bg-white rounded-xl border border-black/[.07] p-8 text-center no-print">
          <div className="text-[14px] text-gray-700 font-medium mb-1">Noch kein Report</div>
          <div className="text-[12.5px] text-gray-500">Klick auf „Report erstellen", um die Ergebnisse aller Agenten zusammenzufassen.</div>
        </div>
      ) : (
        <ReportView report={report} project={current} accent={accent} logo={logo} />
      )}
    </div>
  );
}
